// @flow
import { put, call, takeLatest, select } from 'redux-saga/effects';
import { fetchVirtualShelfSuccess, fetchVirtualShelfError } from '../actions/virtual-shelf.js';
import { FETCH_VSHELF, NOTIFY_USER, FETCH_AFFL_SKUS } from '../actions/types';
import { getVirtualShelf, notifyUser as notifyUserAPI, getAffiliateData as getAffiliateDataAPI } from '../services/virtual-shelf';
import { processCartVSku } from '../utils';
import { showErrorMsg, showSuccessMsg } from '../actions/snackbar';
import { saveCartSuccess } from '../actions/cart';
import { validateSession } from './session';
import { NOTIFY_ACTION, VSHELF_MASTER_LIST, MACHINE_SHELF } from '../constants';
import config from '../config';

function* fetchVirtualShelf(action) {
	try {
		const { payload = {} } = action;
		const { sessionId } = yield select((state) => state.auth || {});
		const { selectedPod = {}, affiliateId } = payload;
		let virtualShelf = yield call(validateSession, getVirtualShelf, affiliateId ? selectedPod.imei : selectedPod.ds_imei, selectedPod.mobile, sessionId);
		const { products_data, virtual_shelf_list } = virtualShelf;
		const skus = new Map<string, Map<string, { [key: string]: mixed }>>();
		const virtualShelfList = new Map();
		const shelfSkuMap = new Map<string, Set<string>>();
		const masterMap = new Map();
		Object.entries(virtual_shelf_list).forEach(([key, vShelf]) => {
			const products = products_data[key].filter(({ qty, action }) => action === NOTIFY_ACTION || +qty > 0);

			if (products.length > 0) {
				skus.set(
					key,
					new Map(
						products.map((sku, index) => {
							const { virtual_is_offer, virtual_offer_label, virtual_offer_price, virtual_is_sampling } = sku;
							if (virtual_is_offer) {
								sku.is_offer = 1;
								sku.offer_price = virtual_offer_price;
								sku.display_offers = virtual_offer_label;
								sku.is_sample = virtual_is_sampling;
							}

							const skuKey = 'v' + sku.sku_id;
							const skuData = {
								...sku,
								isVirtualSku: true,
								vShelfLabel: (vShelf: any).section_label,
								vShelfKey: key,
								key: skuKey,
							};

							const skuMap = shelfSkuMap.get(skuKey);
							if (!skuMap) {
								shelfSkuMap.set(skuKey, new Set([key, VSHELF_MASTER_LIST]));
								masterMap.set(skuKey, { ...skuData, vShelfKey: VSHELF_MASTER_LIST });
							} else {
								shelfSkuMap.set(skuKey, skuMap.add(key));
							}
							return [skuKey, skuData];
						})
					)
				);
				virtualShelfList.set(key, {
					...vShelf,
					key,
					virtualShelf: true,
					label: (vShelf: any).section_label,
					count: products.length,
				});
			}
		});

		if (masterMap.size > 0) {
			skus.set(VSHELF_MASTER_LIST, masterMap);
			virtualShelfList.set(VSHELF_MASTER_LIST, {
				key: VSHELF_MASTER_LIST,
				virtualShelf: true,
				label: 'All Products',
				count: masterMap.size,
			});
		}

		const cart = yield select((state) => state.cart);
		if (cart && cart.imei === selectedPod.imei) {
			const { cartState, cartUpdated } = processCartVSku(skus, cart, shelfSkuMap);
			if (cartUpdated) {
				yield put(saveCartSuccess(cartState));
				yield put(
					showErrorMsg(`Cart changed due to unavailability.
		      Please click on cart to check changes`)
				);
			}
		}

	  // remove once api integration done
		let { anchorDummyData: anchors = [] } = config;
		if (anchors && anchors.length > 0) {
			anchors = anchors.filter(({target_shelf}) => target_shelf === MACHINE_SHELF || virtualShelfList.get(String(target_shelf)))	
		}

		yield put(fetchVirtualShelfSuccess({ skus, virtualShelfList, shelfSkuMap, anchors }));
	} catch (error) {
		yield put(fetchVirtualShelfError(error));
		yield put(showErrorMsg(error.message));
	}
}

function* notifyUser(action) {
	try {
		const { payload = {} } = action;
		const { sessionId } = yield select((state) => state.auth || {});
		yield call(validateSession, notifyUserAPI, payload, sessionId);
		yield put(showSuccessMsg('Thank You! We will notify you as soon as it is available.'));
	} catch (error) {
		yield put(showErrorMsg(error.message));
	}
}

function* fetchAffiliateData(action) {
	try {
		const { payload = {} } = action;
		const { sessionId } = yield select((state) => state.auth || {});
		let virtualShelf = yield call(validateSession, getAffiliateDataAPI, payload.imei, payload.affiliateID, payload.mobile, sessionId);
		const { products_data, virtual_shelf_list } = virtualShelf;
		const skus = new Map<string, Map<string, { [key: string]: mixed }>>();
		const virtualShelfList = new Map();
		const shelfSkuMap = new Map<string, Set<string>>();
		const masterMap = new Map();
		Object.entries(virtual_shelf_list).forEach(([key, vShelf]) => {
			const products = products_data[key].filter(({ qty, action }) => action === NOTIFY_ACTION || +qty > 0);

			if (products.length > 0) {
				skus.set(
					key,
					new Map(
						products.map((sku, index) => {
							const { virtual_is_offer, virtual_offer_label, virtual_offer_price, virtual_is_sampling } = sku;
							if (virtual_is_offer) {
								sku.is_offer = 1;
								sku.offer_price = virtual_offer_price;
								sku.display_offers = virtual_offer_label;
								sku.is_sample = virtual_is_sampling;
							}

							const skuKey = 'v' + sku.sku_id;
							const skuData = {
								...sku,
								isVirtualSku: true,
								vShelfLabel: (vShelf: any).section_label,
								vShelfKey: key,
								key: skuKey,
							};

							const skuMap = shelfSkuMap.get(skuKey);
							if (!skuMap) {
								shelfSkuMap.set(skuKey, new Set([key, VSHELF_MASTER_LIST]));
								masterMap.set(skuKey, { ...skuData, vShelfKey: VSHELF_MASTER_LIST });
							} else {
								shelfSkuMap.set(skuKey, skuMap.add(key));
							}
							return [skuKey, skuData];
						})
					)
				);
				virtualShelfList.set(key, {
					...vShelf,
					key,
					virtualShelf: true,
					label: (vShelf: any).section_label,
					count: products.length,
				});
			}
		});

		if (masterMap.size > 0) {
			skus.set(VSHELF_MASTER_LIST, masterMap);
			virtualShelfList.set(VSHELF_MASTER_LIST, {
				key: VSHELF_MASTER_LIST,
				virtualShelf: true,
				label: 'All Products',
				count: masterMap.size,
			});
		}

		const cart = yield select((state) => state.cart);
		if (cart && cart.imei === payload.imei) {
			const { cartState, cartUpdated } = processCartVSku(skus, cart, shelfSkuMap);
			if (cartUpdated) {
				yield put(saveCartSuccess(cartState));
				yield put(
					showErrorMsg(`Cart changed due to unavailability.
		      Please click on cart to check changes`)
				);
			}
		}

	  // remove once api integration done
		let { anchorDummyData: anchors = [] } = config;
		if (anchors && anchors.length > 0) {
			anchors = anchors.filter(({target_shelf}) => target_shelf === MACHINE_SHELF || virtualShelfList.get(String(target_shelf)))	
		}

		yield put(fetchVirtualShelfSuccess({ skus, virtualShelfList, shelfSkuMap, anchors }));
	} catch (error) {
		yield put(fetchVirtualShelfError(error));
		yield put(showErrorMsg(error.message));
	}
}

export function* watchVirtualShelf(): Iterable<any> {
	yield takeLatest(FETCH_VSHELF, fetchVirtualShelf);
	yield takeLatest(NOTIFY_USER, notifyUser);
	yield takeLatest(FETCH_AFFL_SKUS, fetchAffiliateData);
}
